/* ONE BOUT — <ob-featured-tabs> (sections/ob-featured-tabs.liquid, Sprint 2 / H1; loop carousel 2026-09-25).
   Tabs (WAI-ARIA tabs pattern: click, ←/→/Home/End) toggle server-rendered panels; the button follows the active
   tab's collection unless a fixed link is set.
   Each panel's track (.ob-featured__track) is an INFINITE carousel: the server-rendered cards (the "set") are
   cloned k× before and after (k = enough sets to cover ≥ 2 viewports on each side), the scroll position is kept
   inside the real set's range [k·setW, (k+1)·setW) and teleported by ±setW when it leaves it — pixel-identical, so
   the loop is invisible. Native scroll + scroll-snap drive touch / trackpad / wheel / keyboard; mouse drag is our
   own (incremental deltas, so a teleport mid-drag is harmless — Prestige's <scroll-carousel allow-drag> anchors to
   the pointer-down position and would snap back). On touch the teleport waits for the gesture to settle (iOS kills
   momentum when scrollLeft is set mid-flick); the next touchstart wraps immediately, and there is an emergency wrap
   near the DOM edges. Optional autoplay = slow continuous drift (section setting; paused on hover / focus / touch /
   off-screen; off with prefers-reduced-motion). Clones are aria-hidden and unfocusable, ids stripped.
   SV15/16 (Svitlana 30. 9.): the row STARTS aligned to the container's left edge with an empty gutter — the leading
   clones stay invisible (.is-pristine) until the row is first moved (drag, swipe, wheel, arrows, autoplay); from then
   on the cards pass into the left bleed as before.
   4. 10. (Jakub): images are loaded + decoded once the row is within a viewport of the screen (warm()), so a fast
   trackpad flick no longer shows empty cards. */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ObLoopTrack {
  constructor(track, thumb, host) {
    this.track = track;
    this.thumb = thumb;
    this.host = host;
    this.cells = Array.from(track.children).filter((el) => el.classList.contains('ob-featured__cell'));
    this.n = this.cells.length;
    this.k = 0;
    this.step = 0;
    this.setW = 0;
    this.lo = 0;
    this.ready = false;
    this.touching = false;
    this.coasting = false;
    this.drag = null;
    this.suppressClick = false;
    this.autoplay = host.dataset.autoplay === 'true' && this.n > 1;
    this.speed = Math.max(5, parseFloat(host.dataset.autoplaySpeed) || 30);   // px / s
    if (this.autoplay) track.style.scrollSnapType = 'none';   // a drifting row never snaps (no jolt when it pauses)
    this.playing = false;
    this.pos = 0;
    this.last = 0;
    this.hovered = false;
    this.focused = false;
    this.inView = false;
    this.pristine = true;
    this.home = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.tick = this.tick.bind(this);
    this.settle = this.settle.bind(this);

    track.addEventListener('scroll', this.onScroll, { passive: true });
    track.addEventListener('scrollend', this.settle);
    track.addEventListener('touchstart', () => { this.touching = true; this.coasting = false; this.pause(); this.wrap(); }, { passive: true });
    track.addEventListener('touchend', () => { this.touching = false; this.coasting = true; this.armSettle(); }, { passive: true });
    track.addEventListener('touchcancel', () => { this.touching = false; this.coasting = true; this.armSettle(); }, { passive: true });
    track.addEventListener('pointerdown', this.onPointerDown);
    track.addEventListener('pointermove', this.onPointerMove);
    track.addEventListener('pointerup', this.onPointerUp);
    track.addEventListener('pointercancel', this.onPointerUp);
    document.addEventListener('pointerup', this.onPointerUp);   // released outside the track before the drag threshold
    track.addEventListener('click', this.onClickCapture, true);
    track.addEventListener('dragstart', (event) => { if (this.drag) event.preventDefault(); });
    track.addEventListener('keydown', this.onKeyDown);
    track.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovered = true; this.pause(); } });
    track.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') { this.hovered = false; this.resume(); } });
    track.addEventListener('focusin', () => { this.focused = true; this.pause(); });
    track.addEventListener('focusout', () => { this.focused = false; this.resume(); });
    track.addEventListener('wheel', () => this.pause(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(track);
    // warm the images before the row can be flicked (Jakub 4. 10.: a fast trackpad flick showed empty cards) —
    // clones inherit loading="lazy", so far-away cards were only fetched + decoded once already on screen
    this.warmed = false;
    this.warmObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { this.warm(); this.warmObserver.disconnect(); }
    }, { rootMargin: '100% 0px' });
    this.warmObserver.observe(track);
    if (this.autoplay) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.resume(); else this.pause();
      }, { threshold: 0.2 });
      this.intersectionObserver.observe(track);
      REDUCED_MOTION.addEventListener('change', () => this.resume());
    }
    this.layout();
  }

  destroy() {
    this.pause();
    document.removeEventListener('pointerup', this.onPointerUp);
    this.resizeObserver.disconnect();
    this.warmObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
  }

  /* load + decode every primary card image of the track (real cards and clones) ahead of the flick: decoded
     bitmaps are rasterised immediately while scrolling, otherwise Chrome paints the not-yet-ready stretch blank.
     Hover (secondary) images stay lazy. Same URLs for all copies → one request per image, the rest from cache. */
  warm(root = this.track) {
    this.warmed = true;
    root.querySelectorAll('img').forEach((img) => {
      if (img.classList.contains('product-card__image--secondary')) return;
      if (img.loading === 'lazy') img.loading = 'eager';
      const decode = () => { if (img.decode) img.decode().catch(() => {}); };
      if (img.complete && img.naturalWidth) decode();
      else img.addEventListener('load', decode, { once: true });
    });
  }

  /* ---------------------------------------------------------------- layout + clones */
  layout() {
    const track = this.track;
    if (this.n < 2 || track.clientWidth === 0 || !track.offsetParent) { this.updateUi(); return; }
    const phase = this.ready ? this.phase() : 0;
    // fractional geometry (offsetLeft is rounded — 0.5 px per card became a visible misalignment on DEV)
    const left = (el) => el.getBoundingClientRect().left;
    const step = left(this.cells[1]) - left(this.cells[0]);
    if (!(step > 0)) return;
    // snap the cards to the container edge: scroll-padding percentages resolve against the scrollport (the full
    // viewport-wide track), so the bleed has to be set here in px (= the track's own padding-inline-start)
    track.style.scrollPaddingInline = getComputedStyle(track).paddingInlineStart;
    const k = Math.max(1, Math.ceil((track.clientWidth * 2) / (step * this.n)));
    if (k !== this.k) this.buildClones(k);
    // the real set's range, measured from the DOM: lo = first real card − first clone, setW = first trailing clone − first real card
    const lo = left(this.cells[0]) - left(track.firstElementChild);
    const setW = left(track.children[this.k * this.n + this.n]) - left(this.cells[0]);
    const changed = Math.abs(step - this.step) > 0.01 || Math.abs(setW - this.setW) > 0.01 || Math.abs(lo - this.lo) > 0.01;
    this.step = step;
    this.setW = setW;
    this.lo = lo;
    if (!this.ready || changed) {
      this.ready = true;
      this.jump(lo + (Math.round(phase * this.n) % this.n) * step);   // same card at the container edge after a resize
      if (this.pristine) { this.home = track.scrollLeft; track.classList.add('is-pristine'); }
    }
    this.updateUi();
    this.resume();
  }

  buildClones(k) {
    this.track.querySelectorAll('[data-ob-clone]').forEach((el) => el.remove());
    const set = (count) => {
      const fragment = document.createDocumentFragment();
      for (let s = 0; s < count; s++) this.cells.forEach((cell) => fragment.appendChild(this.clone(cell)));
      return fragment;
    };
    const lead = set(k);
    lead.querySelectorAll('[data-ob-clone]').forEach((el) => el.setAttribute('data-ob-clone', 'lead'));
    const trail = set(k);
    const added = [...lead.children, ...trail.children];
    this.track.prepend(lead);
    this.track.append(trail);
    this.k = k;
    if (this.warmed) added.forEach((el) => this.warm(el));
  }

  clone(cell) {
    const el = cell.cloneNode(true);
    el.setAttribute('data-ob-clone', '');
    el.setAttribute('aria-hidden', 'true');
    el.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    el.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
    return el;
  }

  /* ---------------------------------------------------------------- position */
  phase() {
    const rel = (this.track.scrollLeft - this.lo) % this.setW;
    return ((rel + this.setW) % this.setW) / this.setW;
  }

  jump(left) {
    const track = this.track;
    const behavior = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = behavior;
    this.pos = track.scrollLeft;
  }

  /* keep scrollLeft inside the real set's range; returns true when it teleported */
  wrap() {
    if (!this.ready) return false;
    const s = this.track.scrollLeft;
    // 1 px tolerance: a native snap correction of a fraction of a pixel below the range must not count as leaving it
    if (s >= this.lo - 1 && s < this.lo + this.setW - 1) return false;
    this.jump(this.lo + this.phase() * this.setW);
    return true;
  }

  onScroll() {
    if (!this.ready) return;
    if (this.pristine && Math.abs(this.track.scrollLeft - this.home) > 1) {
      this.pristine = false;
      this.track.classList.remove('is-pristine');
    }
    if (this.touching || this.coasting) {
      // a flick may not be teleported mid-momentum (iOS) — unless it is about to hit the end of the clones
      const track = this.track;
      const margin = track.clientWidth * 0.75;
      if (track.scrollLeft < margin || track.scrollLeft > track.scrollWidth - track.clientWidth - margin) this.wrap();
      this.armSettle();
    } else {
      this.wrap();
      if (!this.playing) this.pos = this.track.scrollLeft;
    }
    this.updateUi();
  }

  armSettle() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.settle, 160);
  }

  settle() {
    clearTimeout(this.settleTimer);
    if (this.touching) return;
    this.coasting = false;
    this.wrap();
    this.pos = this.track.scrollLeft;
    this.resume();
  }

  /* nearest card-aligned scroll position (exact, from the measured range) shifted by `count` cards */
  snapPosition(left, count) {
    return this.lo + (Math.round((left - this.lo) / this.step) + count) * this.step;
  }

  scrollByCards(count) {
    if (!this.ready) return;
    this.pause();
    this.track.scrollTo({ left: this.snapPosition(this.track.scrollLeft, count), behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    this.armSettle();
  }

  /* ---------------------------------------------------------------- mouse drag */
  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.ready) return;
    this.pause();
    this.drag = { id: event.pointerId, x: event.clientX, startX: event.clientX, t: event.timeStamp, vx: 0, moved: false };
    this.track.style.scrollSnapType = 'none';
    // no pointer capture yet: capturing on pointerdown retargets the click to the track, so a plain click on a card
    // would never reach its link — the capture starts once the pointer has moved past the drag threshold
  }

  onPointerMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    const dt = Math.max(1, event.timeStamp - drag.t);
    if (!drag.moved && Math.abs(event.clientX - drag.startX) > 4) {
      drag.moved = true;
      this.track.classList.add('is-dragging');
      try { this.track.setPointerCapture(drag.id); } catch (e) { /* not supported */ }
    }
    if (!drag.moved) return;
    event.preventDefault();
    this.track.scrollLeft -= dx;                       // incremental: a wrap() in between does not matter
    drag.vx = drag.vx * 0.6 + (dx / dt) * 0.4;         // px / ms, smoothed
    drag.x = event.clientX;
    drag.t = event.timeStamp;
  }

  onPointerUp(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.drag = null;
    this.track.classList.remove('is-dragging');
    if (!this.autoplay) this.track.style.scrollSnapType = '';
    if (drag.moved) {
      this.suppressClick = true;
      setTimeout(() => { this.suppressClick = false; }, 50);
      const fling = REDUCED_MOTION.matches ? 0 : drag.vx * 180;               // ≈ the distance of a short flick
      this.track.scrollTo({ left: this.snapPosition(this.track.scrollLeft - fling, 0), behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    }
    this.armSettle();
  }

  onClickCapture(event) {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onKeyDown(event) {
    if (event.target !== this.track) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); this.scrollByCards(1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.scrollByCards(-1); }
  }

  /* ---------------------------------------------------------------- autoplay drift */
  canPlay() {
    return this.autoplay && this.ready && this.inView && !this.hovered && !this.focused && !this.touching &&
      !this.coasting && !this.drag && !REDUCED_MOTION.matches && !document.hidden;
  }

  resume() {
    if (this.playing || !this.canPlay()) return;
    this.playing = true;
    this.pos = this.track.scrollLeft;
    this.last = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  pause() {
    if (!this.playing) return;
    this.playing = false;
    cancelAnimationFrame(this.raf);
  }

  tick(now) {
    if (!this.playing) return;
    if (!this.canPlay()) { this.pause(); return; }
    if (this.last) {
      const dt = Math.min(64, now - this.last);
      this.pos += (this.speed * dt) / 1000;
      if (this.pos >= this.lo + this.setW) this.pos -= this.setW;
      this.track.scrollLeft = Math.round(this.pos);
    }
    this.last = now;
    this.raf = requestAnimationFrame(this.tick);
  }

  /* ---------------------------------------------------------------- progress line (mobile) */
  updateUi() {
    if (!this.thumb) return;
    const bar = this.thumb.parentElement;
    if (!this.ready) { bar.hidden = true; return; }
    const ratio = Math.min(1, this.track.clientWidth / this.setW);
    bar.hidden = ratio >= 1;
    this.thumb.style.width = `${ratio * 100}%`;
    this.thumb.style.transform = `translateX(${this.phase() * (1 / ratio - 1) * 100}%)`;
  }
}

class ObFeaturedTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('.ob-featured__panel'));
    this.cta = this.querySelector('[data-ob-auto-link]');

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKey(event, i));
    });
    this.addEventListener('shopify:block:select', (event) => {
      const i = this.tabs.indexOf(event.target);
      if (i > -1) this.select(i);
    });

    // 19. 10. 2026 (Jakub, "Doplňte svůj look"): with data-loop-min, a row with fewer cards is a plain scroll row —
    // no clones (2 products used to show as 4), no progress line. Hosts without the attribute keep the loop.
    const loopMin = parseInt(this.dataset.loopMin, 10) || 0;
    this.loops = this.panels.map((panel) => {
      const track = panel.querySelector('.ob-featured__track');
      if (!track) return null;
      if (track.querySelectorAll(':scope > .ob-featured__cell').length < loopMin) {
        panel.classList.add('is-static');
        const progress = panel.querySelector('.ob-featured__progress');
        if (progress) progress.style.display = 'none';
        return null;
      }
      return new ObLoopTrack(track, panel.querySelector('.ob-featured__progress-thumb'), this);
    });
    document.addEventListener('visibilitychange', () => this.loops.forEach((loop) => loop && (document.hidden ? loop.pause() : loop.resume())));
  }

  disconnectedCallback() {
    (this.loops || []).forEach((loop) => loop && loop.destroy());
  }

  onKey(event, i) {
    const last = this.tabs.length - 1;
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(next > last ? 0 : next < 0 ? last : next, true);
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    if (this.cta && this.tabs[index]) this.cta.href = this.tabs[index].dataset.url;
    this.loops.forEach((loop, i) => { if (loop) { if (i === index) loop.layout(); else loop.pause(); } });
  }
}

if (!customElements.get('ob-featured-tabs')) {
  customElements.define('ob-featured-tabs', ObFeaturedTabs);
}
